import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';

import { onAddQuestionOrRowFillers } from '@webapp/account/src/resources/questions/fillers';
import { addStateFetch } from '@webapp/account/src/resources/store-utils';

import { getParamsData } from '@webapp/common/lib/params';
import { replaceInArray, updateItemInArray } from '@webapp/common/lib/utils';
import type { Question } from '@webapp/common/resources/entities/question';
import type { NestedItem } from '@webapp/common/resources/question';

import { addCarryForwardLogic, deleteCarryForwardLogic, editCarryForwardLogic } from '../carry-forward/slice';

import {
    addQuestion,
    addQuestionCategories,
    addQuestionCols,
    addQuestionRow,
    addQuestionRows,
    changeQuestionCategories,
    changeQuestionCol,
    changeQuestionRow,
    changeQuestionRows,
    changeQuestionType,
    getAllQuestions,
    getConjointConfig,
    getNested,
    moveQuestionToPage,
    nestedAdd,
    nestedDelete,
    removeQuestion,
    removeQuestionCategories,
    removeQuestionCol,
    removeQuestionRow,
    reorderQuestionCategories,
    reorderQuestionCols,
    reorderQuestionRows,
    saveConjointConfig,
    saveQuestion,
    saveQuestionParam,
} from './async';
import { setQuestionFetch } from './lib';
import { createSelectTypeQuestion, createTypeSelectQuestion } from './questions';
import type { QuestionsState } from './state';
import { questionsInitial } from './state';

enum CellType {
    ROWS = 'rows',
    COLS = 'cols',
    CATEGORIES = 'categories',
}

const addCell = ({ items }: QuestionsState, { cellParams, id, questionId }: any, cell: CellType): void => {
    updateItemInArray(items, questionId, (i: any) => {
        const newItem = {
            ...onAddQuestionOrRowFillers[i.type][cell][0],
            ...cellParams,
            id,
            order: cellParams && cellParams.order ? cellParams.order : i[cell].length + 1,
            isNew: true,
        };

        if (cellParams) {
            newItem.value = cellParams.value || '';
            newItem.value2 = cellParams.value2 || '';
            newItem.value3 = cellParams.value3 || '';
        } else {
            newItem.value = '';
            newItem.value2 = '';
            newItem.value3 = '';
        }

        i[cell].push(newItem);
    });
};

const removeCell = ({ items }: QuestionsState, { cellId, questionId }: any, cellType: CellType): void => {
    const ids = new Set(Array.isArray(cellId) ? cellId : [cellId]);
    updateItemInArray(items, questionId, (q: any) => {
        q[cellType] = q[cellType].filter(({ id }: { id: number }): boolean => !ids.has(id));
    });
};

const changeCell = (
    { items }: QuestionsState,
    {
        cellId,
        field,
        questionId,
        value,
    }: { cellId: number; questionId: number; field?: string | object; value?: unknown },
    cell: CellType,
): void => {
    updateItemInArray(items, questionId, (q: any) => {
        updateItemInArray(q[cell], cellId, (c: any) => {
            if (typeof field === 'string') {
                c[field] = value;
            } else if (isPlainObject(field)) {
                Object.assign(c, field);
            } else if (!field && isPlainObject(value)) {
                Object.assign(c, value);
            }
        });
    });
};

const sortCell = <T extends CellType>(
    { items }: QuestionsState,
    { newIndex, oldIndex, questionId }: { newIndex: number; oldIndex: number; questionId: number },
    cell: T,
): void => {
    updateItemInArray(items, questionId, (question: Question) => {
        const c = question[cell].splice(oldIndex, 1)[0];
        question[cell].splice(newIndex, 0, c as any);
        question[cell].forEach((c: { order?: number }, index: number) => {
            c.order = index + 1;
        });
    });
};

const closeCreate = (s: QuestionsState): void => {
    s.items = s.items.filter(({ id }) => id > 0);
};

export const questionsSlice = createSlice({
    name: 'questions',
    initialState: questionsInitial,
    reducers: {
        toggleExpand(state: QuestionsState): void {
            state.expanded = !state.expanded;
        },
        previewMode(state: QuestionsState, { payload: { questionId } }): void {
            state.items.forEach((i) => {
                i.isNew = false;
                i.previewMode = i.id === questionId ? !i.previewMode : true;
            });
        },
        /** Открыть конкретный вопрос на редактирование, остальные свернуть в превью. */
        editQuestion(state: QuestionsState, { payload: { questionId } }): void {
            state.expanded = true;
            state.items.forEach((i) => {
                i.isNew = false;
                i.previewMode = i.id !== questionId;
            });
        },
        toggleKanoRowExpanded(state: QuestionsState, { payload: { questionId, rowId } }): void {
            updateItemInArray(state.items, questionId, (q: Question) => {
                updateItemInArray(q.rows, rowId, (row: any) => {
                    row.isExpanded = !row.isExpanded;
                });
            });
        },
        createQuestion(state, { payload: { afterId, pageId } }) {
            closeCreate(state);

            const newItem: Question = createSelectTypeQuestion(pageId);
            const afterItemIdx = state.items.findIndex(({ id: id }) => id === afterId);

            state.items.splice(afterItemIdx + 1, 0, newItem);
            state.expanded = true;
            state.items.forEach((i) => {
                i.previewMode = true;
            });
        },
        updateQuestion({ items }, { payload: { questionId, field, value } }) {
            updateItemInArray(items, questionId, (q: Question) => {
                q[field] = value;
            });
        },
        closeCreate(state) {
            closeCreate(state);
        },
    },
    extraReducers: (builder) =>
        addStateFetch(
            builder
                .addCase(saveQuestion.pending, ({ items }, { meta: { arg } }) => {
                    const { field, questionId, value: valueRaw } = arg;
                    let value = valueRaw;
                    if (typeof value === 'string' && value.match(/\{Q-([0-9]+)\}/gi)) {
                        value = value.replace(/\{Q-([0-9]+)\}/gi, (_, id) => {
                            const order = items.find((q) => q.id === Number(id))?.order;
                            if (typeof order === 'number') {
                                return `{Q${order}}`;
                            }
                            return '';
                        });
                    }
                    updateItemInArray(items, questionId, (q: Question) => {
                        q[field] = value;
                    });
                })
                .addCase(removeQuestionCol.fulfilled, (s, { meta: { arg } }) => {
                    removeCell(s, { ...arg, cellId: arg.colId }, CellType.COLS);
                })
                .addCase(addQuestionCols.fulfilled, (s, { payload }) => {
                    payload.groups.forEach(({ id, name }: { id: number; name: string }) => {
                        addCell(
                            s,
                            {
                                ...payload,
                                id,
                                cellParams: {
                                    ...payload.cellParams,
                                    name,
                                },
                            },
                            CellType.COLS,
                        );
                    });
                })
                .addCase(removeQuestion.pending, (s: QuestionsState, { meta: { arg } }) => {
                    const { questionId } = arg;
                    setQuestionFetch(s, questionId, true);
                })
                .addCase(removeQuestion.rejected, (s: QuestionsState, { meta: { arg } }) => {
                    const { questionId } = arg;
                    setQuestionFetch(s, questionId, false);
                })
                .addCase(getAllQuestions.fulfilled, (s: QuestionsState, { payload: { pageId, questions } }) => {
                    const newItems = questions.length === 0 ? [createTypeSelectQuestion(pageId)] : questions;
                    const notPreviewModeQ = s.items.find((q) => !q.previewMode);
                    if (!isEqual(newItems, s.items)) {
                        // TODO FIXME purge, called multiple times on load
                        s.items = newItems;
                    }
                    if (notPreviewModeQ) {
                        s.items.forEach((item) => {
                            if (item.id === notPreviewModeQ.id) {
                                item.previewMode = false;
                            }
                        });
                    }
                })
                .addCase(
                    changeQuestionType.fulfilled,
                    ({ items }: QuestionsState, { payload: { question, questionId } }) => {
                        const item = { ...question, previewMode: false };
                        replaceInArray(items, questionId, item);
                    },
                )
                .addCase(saveQuestionParam.pending, ({ items }: QuestionsState, { meta: { arg } }): void => {
                    const { field, questionId, value, params } = arg;

                    updateItemInArray(items, questionId, (q: Question) => {
                        Object.assign(q.params, getParamsData({ field, value, params }));
                    });
                })
                .addCase(saveQuestionParam.fulfilled, (state, { meta: { arg }, payload }) => {
                    if (payload.maxDiffBlockCount) {
                        updateItemInArray(state.items, arg.questionId, (q: any) => {
                            q.maxDiffBlockCount = payload.maxDiffBlockCount;
                        });
                    }
                    if (payload.blockSize) {
                        updateItemInArray(state.items, arg.questionId, (q: any) => {
                            q.params.blockSize = payload.blockSize;
                        });
                    }
                    if (payload.targetRepetitions) {
                        updateItemInArray(state.items, arg.questionId, (q: any) => {
                            q.params.targetRepetitions = payload.targetRepetitions;
                        });
                    }
                    if (payload.targetRepetitionsOptions) {
                        updateItemInArray(state.items, arg.questionId, (q: any) => {
                            q.targetRepetitionsOptions = payload.targetRepetitionsOptions;
                        });
                    }
                })
                .addCase(removeQuestionRow.pending, (s, { meta: { arg } }) => {
                    removeCell(s, { ...arg, cellId: arg.rowId }, CellType.ROWS);
                })
                .addCase(removeQuestionRow.fulfilled, (s, { meta: { arg }, payload }) => {
                    if (payload.maxDiffBlockCount) {
                        updateItemInArray(s.items, arg.questionId, (q: any) => {
                            q.maxDiffBlockCount = payload.maxDiffBlockCount;
                        });
                    }
                    if (payload.blockSize) {
                        updateItemInArray(s.items, arg.questionId, (q: any) => {
                            q.params.blockSize = payload.blockSize;
                        });
                    }
                    if (payload.targetRepetitions) {
                        updateItemInArray(s.items, arg.questionId, (q: any) => {
                            q.params.targetRepetitions = payload.targetRepetitions;
                        });
                    }
                    if (payload.targetRepetitionsOptions) {
                        updateItemInArray(s.items, arg.questionId, (q: any) => {
                            q.targetRepetitionsOptions = payload.targetRepetitionsOptions;
                        });
                    }
                })
                .addCase(
                    addQuestionRows.fulfilled,
                    (
                        { items },
                        {
                            payload: {
                                cells,
                                questionId,
                                replace,
                                maxDiffBlockCount,
                                blockSize,
                                targetRepetitions,
                                targetRepetitionsOptions,
                            },
                        },
                    ) => {
                        updateItemInArray(items, questionId, (q: Question) => {
                            q[CellType.ROWS] = replace ? cells : (q[CellType.ROWS] as any).concat(cells);
                        });
                        if (maxDiffBlockCount) {
                            updateItemInArray(items, questionId, (q: any) => {
                                q.maxDiffBlockCount = maxDiffBlockCount;
                            });
                        }
                        if (blockSize) {
                            updateItemInArray(items, questionId, (q: any) => {
                                q.params.blockSize = blockSize;
                            });
                        }
                        if (targetRepetitions) {
                            updateItemInArray(items, questionId, (q: any) => {
                                q.params.targetRepetitions = targetRepetitions;
                            });
                        }
                        if (targetRepetitionsOptions) {
                            updateItemInArray(items, questionId, (q: any) => {
                                q.targetRepetitionsOptions = targetRepetitionsOptions;
                            });
                        }
                    },
                )
                .addCase(addQuestion.fulfilled, ({ items }: QuestionsState, { payload: { questionId } }): void => {
                    updateItemInArray(items, questionId, (q: Question) => {
                        q.previewMode = false;
                        q.isNew = true;
                    });
                })
                .addCase(addQuestion.pending, (s: QuestionsState, { meta: { arg } }): void => {
                    const { qType, question } = arg;
                    if (qType.children === undefined) {
                        setQuestionFetch(s, question.id, true);
                    }
                })
                .addCase(addQuestion.rejected, (s: QuestionsState, { meta: { arg } }): void => {
                    const {
                        question: { id },
                    } = arg;
                    setQuestionFetch(s, id, false);
                })
                .addCase(reorderQuestionRows.pending, (s, { meta: { arg } }) => {
                    sortCell(s, arg, CellType.ROWS);
                })
                .addCase(reorderQuestionCols.pending, (s, { meta: { arg } }) => {
                    sortCell(s, arg, CellType.COLS);
                })
                .addCase(reorderQuestionCategories.pending, (s, { meta: { arg } }) => {
                    sortCell(s, arg, CellType.CATEGORIES);
                })
                .addCase(
                    addQuestionRow.fulfilled,
                    (
                        s,
                        {
                            meta: {
                                arg: { questionId },
                            },
                            payload,
                        },
                    ) => {
                        addCell(s, payload, CellType.ROWS);
                        if (payload.maxDiffBlockCount) {
                            updateItemInArray(s.items, questionId, (q: any) => {
                                q.maxDiffBlockCount = payload.maxDiffBlockCount;
                            });
                        }
                        if (payload.blockSize) {
                            updateItemInArray(s.items, questionId, (q: any) => {
                                q.params.blockSize = payload.blockSize;
                            });
                        }
                        if (payload.targetRepetitions) {
                            updateItemInArray(s.items, questionId, (q: any) => {
                                q.params.targetRepetitions = payload.targetRepetitions;
                            });
                        }
                        if (payload.targetRepetitionsOptions) {
                            updateItemInArray(s.items, questionId, (q: any) => {
                                q.targetRepetitionsOptions = payload.targetRepetitionsOptions;
                            });
                        }
                    },
                )
                .addCase(changeQuestionRow.pending, (s, { meta: { arg } }) => {
                    let value = arg.value;
                    if (typeof value === 'string' && value.match(/\{Q-([0-9]+)\}/gi)) {
                        value = value.replace(/\{Q-([0-9]+)\}/gi, (_, id) => {
                            const order = s.items.find((q) => q.id === Number(id))?.order;
                            if (typeof order === 'number') {
                                return `{Q${order}}`;
                            }
                            return '';
                        });
                    }
                    changeCell(s, { ...arg, cellId: arg.rowId, value }, CellType.ROWS);
                })
                .addCase(changeQuestionRow.fulfilled, (s, { meta: { arg }, payload }) => {
                    if (payload.maxDiffBlockCount) {
                        updateItemInArray(s.items, arg.questionId, (q: any) => {
                            q.maxDiffBlockCount = payload.maxDiffBlockCount;
                        });
                    }
                    if (payload.blockSize) {
                        updateItemInArray(s.items, arg.questionId, (q: any) => {
                            q.params.blockSize = payload.blockSize;
                        });
                    }
                    if (payload.targetRepetitions) {
                        updateItemInArray(s.items, arg.questionId, (q: any) => {
                            q.params.targetRepetitions = payload.targetRepetitions;
                        });
                    }
                    if (payload.targetRepetitionsOptions) {
                        updateItemInArray(s.items, arg.questionId, (q: any) => {
                            q.targetRepetitionsOptions = payload.targetRepetitionsOptions;
                        });
                    }
                })
                .addCase(changeQuestionCol.pending, (s, { meta: { arg } }) => {
                    changeCell(s, { ...arg, cellId: arg.colId }, CellType.COLS);
                })
                .addCase(
                    changeQuestionRows.pending,
                    (
                        { items },
                        {
                            meta: {
                                arg: { data, questionId },
                            },
                        },
                    ): void => {
                        updateItemInArray(items, questionId, (q: any) => {
                            for (const id of Object.keys(data)) {
                                updateItemInArray(q.rows, Number(id), (c: any) => {
                                    Object.assign(c, data[id]);
                                });
                            }
                        });
                    },
                )
                .addCase(moveQuestionToPage.pending, ({ items }: QuestionsState, { meta: { arg } }): void => {
                    const { pageId, questionId } = arg;
                    updateItemInArray(items, questionId, (q) => {
                        q.pageId = pageId;
                    });
                })
                .addCase(getNested.fulfilled, ({ items }: QuestionsState, { meta: { arg }, payload }): void => {
                    const { level, questionId } = arg;
                    updateItemInArray(items, questionId, (q) => {
                        q.items[level] = payload.items;
                        Object.keys(q.items).forEach((l) => {
                            if (l <= level) return;
                            delete q.items[l];
                        });
                    });
                })
                .addCase(nestedAdd.fulfilled, ({ items }: QuestionsState, { meta: { arg }, payload }): void => {
                    const { answerId, items: strItems, level, parentId, questionId } = arg;
                    const newItems: Array<NestedItem> = payload.id.map((id: number, idx: number) => ({
                        id,
                        name: strItems[idx],
                        parentId,
                        answerId,
                        level,
                    }));

                    updateItemInArray(items, questionId, (q) => {
                        q.items[level] = q.items[level] || [];
                        // @ts-ignore
                        q.items[level] = q.items[level].concat(newItems);
                    });
                })
                .addCase(nestedDelete.fulfilled, ({ items }: QuestionsState, { meta: { arg } }): void => {
                    const { itemId, questionId } = arg;

                    updateItemInArray(items, questionId, (q) => {
                        Object.entries(q.items).forEach(([l]) => {
                            q.items[l] = (q.items[l] as Array<NestedItem>).filter(
                                ({ id, parentId }) => id !== itemId && parentId !== itemId,
                            );
                        });
                    });
                })
                .addCase(
                    addCarryForwardLogic.fulfilled,
                    (state, { payload: { id, questionId, childQuestionId, type } }) => {
                        updateItemInArray(state.items, questionId, (q) => {
                            q.forwardLogic.push({ id, questionId, childQuestionId, checked: true, details: [], type });
                        });
                    },
                )
                .addCase(
                    deleteCarryForwardLogic.fulfilled,
                    (
                        state,
                        {
                            payload: { logicId },
                            meta: {
                                arg: { questionId },
                            },
                        },
                    ) => {
                        updateItemInArray(state.items, questionId, (q) => {
                            q.forwardLogic.splice(
                                q.forwardLogic.findIndex((l) => l.id === logicId),
                                1,
                            );
                        });
                    },
                )
                .addCase(
                    editCarryForwardLogic.fulfilled,
                    (
                        state,
                        {
                            meta: {
                                arg: { logicId, checked, questionId, details },
                            },
                        },
                    ) => {
                        updateItemInArray(state.items, questionId, (q) => {
                            const logicIdx = q.forwardLogic.findIndex((l) => l.id === logicId);
                            if (logicIdx !== -1) {
                                q.forwardLogic[logicIdx] = { ...q.forwardLogic[logicIdx], checked, details };
                            }
                        });
                    },
                )
                .addCase(addQuestionCategories.fulfilled, ({ items }, { payload }): void => {
                    const { categories, questionId } = payload;

                    updateItemInArray(items, questionId, (q: Question) => {
                        q.categories.push(...categories);
                    });
                })
                .addCase(changeQuestionCategories.fulfilled, ({ items }, { meta: { arg } }): void => {
                    const { data, question } = arg;

                    updateItemInArray(items, question, (q: Question) => {
                        q.categories = q.categories.map((item) =>
                            data[item.id] ? { ...item, ...data[item.id] } : item,
                        );
                    });
                })
                .addCase(removeQuestionCategories.fulfilled, ({ items }, { meta: { arg } }): void => {
                    const { id, questionId } = arg;

                    updateItemInArray(items, questionId, (q: Question) => {
                        q.categories = q.categories.filter((category) => !id.includes(category.id));
                    });
                })
                .addMatcher(
                    isAnyOf(saveConjointConfig.fulfilled, getConjointConfig.fulfilled),
                    ({ items }, { meta: { arg }, payload }): void => {
                        const { questionId } = arg;

                        updateItemInArray(items, questionId, (q: Question) => {
                            q.conjointConfig = payload;
                        });
                    },
                ),
        ),
});
